import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Globe, Trash } from "@phosphor-icons/react";

export default function FooterProductsEditor({ settings, updateTheme, savingTheme }) {
  const [rows, setRows] = useState(Array.isArray(settings?.footer_products) ? settings.footer_products : []);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setRows(Array.isArray(settings?.footer_products) ? settings.footer_products : []);
    setDirty(false);
  }, [settings?.footer_products]);

  const update = (idx, key, val) => {
    setRows(rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
    setDirty(true);
  };

  const add = () => {
    setRows([...rows, { name: "", url: "https://" }]);
    setDirty(true);
  };

  const remove = (idx) => {
    setRows(rows.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const save = () => {
    const cleaned = rows
      .map((r) => ({ name: (r.name || "").trim(), url: (r.url || "").trim() }))
      .filter((r) => r.name && r.url);
    const bad = cleaned.find((r) => !/^https?:\/\//i.test(r.url));
    if (bad) {
      toast.error(`URL for "${bad.name}" must start with http:// or https://`);
      return;
    }
    updateTheme({ footer_products: cleaned });
    setDirty(false);
  };

  return (
    <div className="flex items-start gap-4">
      <Globe size={22} color="#00E5FF" weight="duotone" />
      <div className="flex-1">
        <h3 className="font-display text-xl">Footer · Products column</h3>
        <p className="mt-1 text-[#8E8E93] text-sm">
          The list of product links under <span className="text-white">PRODUCTS</span> in the site footer.
          Add, edit, remove anything — change is live the moment you save.
        </p>

        <div className="mt-5 space-y-2" data-testid="footer-products-editor">
          {rows.length === 0 && (
            <div className="font-mono text-[10px] text-[#555] py-2">No products. The footer will fall back to defaults.</div>
          )}
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`footer-product-row-${i}`}>
              <input
                type="text"
                value={r.name || ""}
                onChange={(e) => update(i, "name", e.target.value)}
                placeholder="hr.wisewit.ai"
                className="ww-input col-span-4"
                data-testid={`footer-product-name-${i}`}
              />
              <input
                type="url"
                value={r.url || ""}
                onChange={(e) => update(i, "url", e.target.value)}
                placeholder="https://hr.wisewit.ai"
                className="ww-input col-span-7"
                data-testid={`footer-product-url-${i}`}
              />
              <button
                onClick={() => remove(i)}
                className="col-span-1 text-[#8E8E93] hover:text-[#FF4500] justify-self-end p-2"
                title="Remove"
                data-testid={`footer-product-remove-${i}`}
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button onClick={add} className="btn-ghost" data-testid="footer-product-add">+ Add product link</button>
          <button
            onClick={save}
            disabled={savingTheme || !dirty}
            data-testid="footer-products-save"
            className="ml-auto btn-primary disabled:opacity-50"
          >
            {savingTheme ? "Saving…" : "Save footer products"}
          </button>
        </div>
      </div>
    </div>
  );
}
